import { useCallback, useState } from 'react';
import type {
  TimesheetDeadlineSettings as DeadlineSettingsType,
  UtilisationSettings as UtilisationSettingsType,
  OrganisationTimesheetSettings,
  WorkingProfile,
  PolicyViolationSettings,
  PolicyLimits,
  ApprovalScreenSettings,
  OvertimeSettings,
} from '@/types/timesheetSettings';
import { DEFAULT_TIMESHEET_SETTINGS } from '@/types/timesheetSettings';
import { useSettings } from '@/contexts/SettingsContext';

export const useSettingsFormState = () => {
  const { weekStartDay, setWeekStartDay: setGlobalWeekStartDay } = useSettings();

  const [deadlineSettings, setDeadlineSettings] = useState<DeadlineSettingsType | null>(null);
  const [utilisationSettings, setUtilisationSettings] = useState<UtilisationSettingsType | null>(null);
  const [workingProfile, setWorkingProfile] = useState<WorkingProfile | null>(null);
  const [policyEnforcement, setPolicyEnforcement] = useState<PolicyViolationSettings | null>(null);
  const [policyLimits, setPolicyLimits] = useState<PolicyLimits | undefined>(undefined);
  const [approvalScreen, setApprovalScreen] = useState<ApprovalScreenSettings>(DEFAULT_TIMESHEET_SETTINGS.approvalScreen);

  // Data requirements state - weekStartDay now comes from Settings context
  const [showRole, setShowRole] = useState(false);
  const [requireRole, setRequireRole] = useState(false);
  const [showCostCentre, setShowCostCentre] = useState(true);
  const [requireCostCentre, setRequireCostCentre] = useState(true);
  const [requireDescription, setRequireDescription] = useState<'never' | 'always'>('always');
  const [descriptionType, setDescriptionType] = useState<'row' | 'daily'>('row');

  // Project selection state
  const [projectSelectionScope, setProjectSelectionScope] = useState<'all' | 'assigned'>('all');
  const [showArchivedProjects, setShowArchivedProjects] = useState(false);
  const [showProjectTimeRemaining, setShowProjectTimeRemaining] = useState(true);

  // Time entry validation state
  const [minimumEntryDuration, setMinimumEntryDuration] = useState<'6min' | '15min' | '30min' | '1hour'>('15min');
  const [timeRounding, setTimeRounding] = useState<'none' | '15min' | '30min' | '1hour'>('none');
  const [allowNegativeHours, setAllowNegativeHours] = useState(false);

  // Date range restrictions state
  const [historicalEntryLimit, setHistoricalEntryLimit] = useState(4);
  const [allowFutureDatedEntries, setAllowFutureDatedEntries] = useState<'no' | '1week' | '2weeks' | '4weeks' | 'unlimited'>('2weeks');

  // Public holiday country default
  const [publicHolidayCountryDefault, setPublicHolidayCountryDefault] = useState<string | null>(null);
  const [publicHolidayProject, setPublicHolidayProject] = useState<string | null>(null);
  const [publicHolidayCostCentre, setPublicHolidayCostCentre] = useState<string | null>(null);

  // Smart Features state
  const [enableCopyPreviousWeek, setEnableCopyPreviousWeek] = useState(true);
  const [copyDescriptions, setCopyDescriptions] = useState(true);
  const [copyOnlyBillable, setCopyOnlyBillable] = useState(false);
  const [enableAutoSmartFill, setEnableAutoSmartFill] = useState(true);
  const [enableAiInsights, setEnableAiInsights] = useState(true);

  // Overtime settings state
  const [overtimeSettings, setOvertimeSettings] = useState<OvertimeSettings>(DEFAULT_TIMESHEET_SETTINGS.overtime);

  // Dirty tracking
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [tabsWithChanges, setTabsWithChanges] = useState<Set<string>>(new Set());

  // Helper to mark a tab as having changes
  const markTabAsChanged = useCallback((tabId: string) => {
    setTabsWithChanges(prev => new Set(prev).add(tabId));
    setHasUnsavedChanges(true);
  }, []);

  const handleOvertimeSettingsChange = useCallback((settings: OvertimeSettings) => {
    setOvertimeSettings(settings);
    markTabAsChanged('overtime');
  }, [markTabAsChanged]);

  const handleDeadlineChange = useCallback((settings: DeadlineSettingsType, isValid: boolean) => {
    setDeadlineSettings(settings);
    markTabAsChanged('submissions-deadlines');
  }, [markTabAsChanged]);

  const handleUtilisationChange = useCallback((settings: UtilisationSettingsType, isValid: boolean) => {
    setUtilisationSettings(settings);
    markTabAsChanged('utilisation');
  }, [markTabAsChanged]);

  const handleWorkingProfileChange = useCallback((profile: WorkingProfile) => {
    // Working hours are read-only from Resource Manager
    // This callback is kept for compatibility but should not trigger changes
    // setWorkingProfile(profile);
    // setHasUnsavedChanges(true);
  }, []);

  const handlePolicyEnforcementChange = useCallback((enforcement: PolicyViolationSettings) => {
    setPolicyEnforcement(enforcement);
    markTabAsChanged('working-hours');
  }, [markTabAsChanged]);

  const handlePolicyLimitsChange = useCallback((limits: PolicyLimits | undefined) => {
    setPolicyLimits(limits);
    markTabAsChanged('working-hours');
  }, [markTabAsChanged]);

  const handleApprovalScreenChange = useCallback((settings: ApprovalScreenSettings) => {
    setApprovalScreen(settings);
    markTabAsChanged('working-hours');
  }, [markTabAsChanged]);

  const handlePublicHolidayCountryChange = useCallback((value: string | null) => {
    setPublicHolidayCountryDefault(value);
    if (!value) {
      setPublicHolidayProject(null);
      setPublicHolidayCostCentre(null);
    }
    markTabAsChanged('working-hours');
  }, [markTabAsChanged]);

  const handlePublicHolidayProjectChange = useCallback((value: string | null) => {
    setPublicHolidayProject(value ? String(value) : null);
    // Clear cost centre when project changes (user must re-select)
    setPublicHolidayCostCentre(null);
    markTabAsChanged('working-hours');
  }, [markTabAsChanged]);

  const handlePublicHolidayCostCentreChange = useCallback((value: string | null) => {
    setPublicHolidayCostCentre(value ? String(value) : null);
    markTabAsChanged('working-hours');
  }, [markTabAsChanged]);

  // Data requirements change handlers with unsaved tracking
  const handleWeekStartDayChange = useCallback((day: 0 | 1 | 2 | 3 | 4 | 5 | 6) => {
    setGlobalWeekStartDay(day);
    markTabAsChanged('general');
  }, [setGlobalWeekStartDay, markTabAsChanged]);

  const handleShowRoleChange = useCallback((show: boolean) => {
    setShowRole(show);
    markTabAsChanged('general');
  }, [markTabAsChanged]);

  const handleRequireRoleChange = useCallback((require: boolean) => {
    setRequireRole(require);
    markTabAsChanged('general');
  }, [markTabAsChanged]);

  const handleShowCostCentreChange = useCallback((show: boolean) => {
    setShowCostCentre(show);
    markTabAsChanged('general');
  }, [markTabAsChanged]);

  const handleRequireCostCentreChange = useCallback((require: boolean) => {
    setRequireCostCentre(require);
    markTabAsChanged('general');
  }, [markTabAsChanged]);

  const handleRequireDescriptionChange = useCallback((value: 'never' | 'always') => {
    setRequireDescription(value);
    markTabAsChanged('general');
  }, [markTabAsChanged]);

  const handleDescriptionTypeChange = useCallback((value: 'row' | 'daily') => {
    setDescriptionType(value);
    markTabAsChanged('general');
  }, [markTabAsChanged]);

  // Project selection change handlers
  const handleProjectSelectionScopeChange = useCallback((scope: 'all' | 'assigned') => {
    setProjectSelectionScope(scope);
    markTabAsChanged('general');
  }, [markTabAsChanged]);

  const handleShowArchivedProjectsChange = useCallback((show: boolean) => {
    setShowArchivedProjects(show);
    markTabAsChanged('general');
  }, [markTabAsChanged]);

  const handleShowProjectTimeRemainingChange = useCallback((show: boolean) => {
    setShowProjectTimeRemaining(show);
    markTabAsChanged('general');
  }, [markTabAsChanged]);

  // Time entry validation change handlers
  const handleMinimumEntryDurationChange = useCallback((duration: '6min' | '15min' | '30min' | '1hour') => {
    setMinimumEntryDuration(duration);
    markTabAsChanged('general');
  }, [markTabAsChanged]);

  const handleTimeRoundingChange = useCallback((rounding: 'none' | '15min' | '30min' | '1hour') => {
    setTimeRounding(rounding);
    markTabAsChanged('general');
  }, [markTabAsChanged]);

  const handleAllowNegativeHoursChange = useCallback((allow: boolean) => {
    setAllowNegativeHours(allow);
    markTabAsChanged('general');
  }, [markTabAsChanged]);

  const handleHistoricalEntryLimitChange = useCallback((weeks: number) => {
    setHistoricalEntryLimit(weeks);
    markTabAsChanged('general');
  }, [markTabAsChanged]);

  const handleAllowFutureDatedEntriesChange = useCallback((allow: 'no' | '1week' | '2weeks' | '4weeks' | 'unlimited') => {
    setAllowFutureDatedEntries(allow);
    markTabAsChanged('general');
  }, [markTabAsChanged]);

  // Smart Features change handlers
  const handleEnableCopyPreviousWeekChange = useCallback((enable: boolean) => {
    setEnableCopyPreviousWeek(enable);
    markTabAsChanged('general');
  }, [markTabAsChanged]);

  const handleCopyDescriptionsChange = useCallback((copy: boolean) => {
    setCopyDescriptions(copy);
    markTabAsChanged('general');
  }, [markTabAsChanged]);

  const handleCopyOnlyBillableChange = useCallback((copy: boolean) => {
    setCopyOnlyBillable(copy);
    markTabAsChanged('general');
  }, [markTabAsChanged]);

  const handleEnableAutoSmartFillChange = useCallback((enable: boolean) => {
    setEnableAutoSmartFill(enable);
    markTabAsChanged('general');
  }, [markTabAsChanged]);

  const handleEnableAiInsightsChange = useCallback((enable: boolean) => {
    setEnableAiInsights(enable);
    markTabAsChanged('general');
  }, [markTabAsChanged]);

  // Applies loaded settings to every editable field and clears dirty tracking.
  // weekStartDay is managed by Settings context, so it is not applied here.
  const hydrate = useCallback((settings: OrganisationTimesheetSettings) => {
    setDeadlineSettings(settings.deadline);
    setUtilisationSettings(settings.utilisation);

    setShowRole(settings.showRole);
    setRequireRole(settings.requireRole);
    setShowCostCentre(settings.showCostCentre);
    setRequireCostCentre(settings.requireCostCentre);

    // Smart features settings live on the deadline settings
    setEnableCopyPreviousWeek(settings.deadline.enableCopyPreviousWeek);
    setCopyDescriptions(settings.deadline.copyDescriptions);
    setCopyOnlyBillable(settings.deadline.copyOnlyBillable);
    setEnableAutoSmartFill(settings.deadline.enableAutoSmartFill ?? true);
    setEnableAiInsights(settings.deadline.enableAiInsights ?? true);

    setRequireDescription(settings.requireDescription ?? 'always');
    setDescriptionType(settings.descriptionType ?? 'row');
    setProjectSelectionScope(settings.projectSelectionScope ?? 'all');
    setShowArchivedProjects(settings.showArchivedProjects ?? false);
    setShowProjectTimeRemaining(settings.showProjectTimeRemaining ?? true);
    setMinimumEntryDuration(settings.minimumEntryDuration ?? '6min');
    setTimeRounding(settings.timeRounding ?? 'none');
    setAllowNegativeHours(settings.allowNegativeHours ?? false);
    setHistoricalEntryLimit(settings.historicalEntryLimit ?? 4);
    setAllowFutureDatedEntries(settings.allowFutureDatedEntries ?? '2weeks');
    setPublicHolidayCountryDefault(settings.publicHolidayCountryDefault ?? null);
    setPublicHolidayProject(settings.publicHolidayProject ?? null);
    setPublicHolidayCostCentre(settings.publicHolidayCostCentre ?? null);

    setHasUnsavedChanges(false);
    setTabsWithChanges(new Set());

    setPolicyEnforcement(settings.policyViolations || {
      monitoringFrequency: 'week',
      suppressDailyWhenWeeklyExceeded: true,
      consolidateDailyWarnings: true,
      checks: {
        'daily-overage': { enabled: true, threshold: 1.25, severity: 'warning' },
        'weekly-overage': { enabled: true, threshold: 1.10, severity: 'warning' },
        'legal-daily-maximum': { enabled: false, severity: 'critical' },
        'legal-weekly-maximum': { enabled: false, severity: 'critical' },
        'missing-hours': { enabled: true, threshold: 0.80, severity: 'info' },
        'non-working-day': { enabled: true, severity: 'info' },
        'daily-missing-hours': { enabled: true, threshold: 0.80, severity: 'info' },
        'weekly-missing-hours': { enabled: true, threshold: 0.80, severity: 'info' },
        'daily-non-working-day': { enabled: true, severity: 'info' },
        'weekly-non-working-day': { enabled: true, severity: 'info' }
      },
      displayMode: 'smart',
      showInfoAlerts: true,
      preset: 'balanced'
    });
    setPolicyLimits(settings.policyLimits);
    setApprovalScreen(settings.approvalScreen ?? DEFAULT_TIMESHEET_SETTINGS.approvalScreen);
    setOvertimeSettings(settings.overtime ?? DEFAULT_TIMESHEET_SETTINGS.overtime);
  }, []);

  return {
    weekStartDay, setGlobalWeekStartDay,

    // Field state
    deadlineSettings, utilisationSettings, workingProfile, policyEnforcement, policyLimits, approvalScreen,
    showRole, requireRole, showCostCentre, requireCostCentre, requireDescription, descriptionType,
    projectSelectionScope, showArchivedProjects, showProjectTimeRemaining,
    minimumEntryDuration, timeRounding, allowNegativeHours,
    historicalEntryLimit, allowFutureDatedEntries,
    publicHolidayCountryDefault, publicHolidayProject, publicHolidayCostCentre,
    enableCopyPreviousWeek, copyDescriptions, copyOnlyBillable, enableAutoSmartFill, enableAiInsights,
    overtimeSettings,

    // Dirty tracking
    hasUnsavedChanges, tabsWithChanges, markTabAsChanged,

    // Raw setters still needed by the page's loaders / savers / health-check fixes
    setDeadlineSettings, setUtilisationSettings, setWorkingProfile, setPolicyEnforcement, setPolicyLimits,
    setApprovalScreen, setShowRole, setMinimumEntryDuration, setAllowFutureDatedEntries, setEnableAiInsights,
    setHistoricalEntryLimit, setAllowNegativeHours, setEnableCopyPreviousWeek,
    setHasUnsavedChanges, setTabsWithChanges,

    hydrate,

    // Change handlers
    handleOvertimeSettingsChange, handleDeadlineChange, handleUtilisationChange, handleWorkingProfileChange,
    handlePolicyEnforcementChange, handlePolicyLimitsChange, handleApprovalScreenChange,
    handlePublicHolidayCountryChange, handlePublicHolidayProjectChange, handlePublicHolidayCostCentreChange,
    handleWeekStartDayChange, handleShowRoleChange, handleRequireRoleChange,
    handleShowCostCentreChange, handleRequireCostCentreChange,
    handleRequireDescriptionChange, handleDescriptionTypeChange,
    handleProjectSelectionScopeChange, handleShowArchivedProjectsChange, handleShowProjectTimeRemainingChange,
    handleMinimumEntryDurationChange, handleTimeRoundingChange, handleAllowNegativeHoursChange,
    handleHistoricalEntryLimitChange, handleAllowFutureDatedEntriesChange,
    handleEnableCopyPreviousWeekChange, handleCopyDescriptionsChange, handleCopyOnlyBillableChange,
    handleEnableAutoSmartFillChange, handleEnableAiInsightsChange,
  };
};

export type SettingsFormState = ReturnType<typeof useSettingsFormState>;
